import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap, toArray } from 'rxjs/operators';

import { AlcmeonContextService } from '../core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from '../core/async-request/async-request.service';
import { API_URL_PREFIX, JSON_CONTENT_TYPE_HEADER } from '../shared/services/basic.service';
import { ErrorHandlingService } from '../shared/services/error-handling.service';
import { MediasService } from '../shared/services/medias.service';
import {
  ApplyUserActionResponse,
  CRMContactInfo,
  CRMFieldDescription,
  CRMFieldForProfile,
  CRMOrg,
  CRMorgAndLinkedCRMData,
  ExternalUser,
  LinkedCRMDataById,
  LinkedSite,
  LinkedUserWithCRMOrgs,
} from './shared/models';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private alcmeonContext: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private mediasService: MediasService
  ) {}

  getUserProfile(userId: number): Observable<ExternalUser> {
    const companyId = this.alcmeonContext.companyId;
    const taskForProcessingURL = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/external-users/${userId}`;
    const options = {
      ...JSON_CONTENT_TYPE_HEADER,
      params: {
        fields: 'id,name,external_url,details,actions_allowed',
      },
    };
    return this.http.get<ExternalUser>(taskForProcessingURL, options).pipe(
      tap(user => this.removeVIPActions(user)),
      this.errorHandlingService.catchAlertAndThrowHttpError()
    );
  }

  applyUserAction(siteId: number, taskId: string, userId: number, actionType: string): Observable<ExternalUser> {
    const companyId = this.alcmeonContext.companyId;
    const data = {
      site_id: siteId,
      task_id: taskId,
      action_type: actionType,
    };
    const url =
      `${API_URL_PREFIX}/api/1.0/companies/${companyId}/external-users/${userId}/actions` +
      `?include_refreshed_allowed_actions=true`;
    return this.http.post<ApplyUserActionResponse>(url, data, JSON_CONTENT_TYPE_HEADER).pipe(
      map(response => response.user),
      tap(user => this.removeVIPActions(user)),
      catchError(response => this.handleUserActiondError(response))
    );
  }

  setGroupName(siteId: number, userId: number, groupName: string) {
    const companyId = this.alcmeonContext.companyId;
    const url = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/sites/${siteId}/linked-users/${userId}/group-name`;
    const data = {
      group_name: groupName,
    };
    return this.http
      .put<any>(url, data, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Calls /linked-users to get a list of CRM orgs available for the given site,
   * and for each org, fetches the CRM contact (if any) corresponding to the given user.
   * The CRM objects and fields decsriptions are converted to a more friendly format.
   */
  getCRMContactInfos(siteId: number, userId: number): Observable<CRMContactInfo[]> {
    // This one took me a long while to figure out.
    // I had a first version that used forkJoin, which was not 100% satisfactory
    // (it had too much depth of parenthesis nesting to my liking,
    //  see changeset e73a1f797cf2 if you are really curious)
    // I finally figured, thanks to https://stackoverflow.com/a/53237377,
    // that mergeMap can be passed a function that returns an array (and not an Observable),
    // because Array is a kind of ObservableInput. This use of mergeMap
    // (the last mergeMap below, which converts an array to an Observable that emits all the array elements)
    // is not mentionned in rxjs API doc
    // (although you could figure it out by looking at the method signature, I guess)
    return this.getLinkedUsersAndCrmOrgs(siteId, userId).pipe(
      map((linkedUser: LinkedUserWithCRMOrgs) => {
        return linkedUser.crm_orgs.filter((org: CRMOrg) => {
          return org.linked_sites.some(
            (linkedSite: LinkedSite) => linkedSite.site_id === siteId && linkedSite.is_active
          );
        });
      }),
      mergeMap((filteredOrgs: CRMOrg[]) => this.getLinkedCRMContactList(userId, filteredOrgs)),
      mergeMap((crmOrgAndLinkedCRMData: CRMorgAndLinkedCRMData[]) => crmOrgAndLinkedCRMData),
      map((crmData: CRMorgAndLinkedCRMData) => this.adaptCRMData(crmData)),
      toArray()
    );
  }

  exportConversationToExcel(taskId: number) {
    const url = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContext.companyId}/tasks/${taskId}/exports`;
    this.mediasService.downloadFile(url);
  }

  private adaptCRMData({ organization, crmData }: CRMorgAndLinkedCRMData): CRMContactInfo {
    const result: CRMContactInfo = {
      organizationId: organization.id,
      organizationType: organization.type,
      organizationDisplayType: organization.type.charAt(0).toLocaleUpperCase() + organization.type.slice(1),
      organizationName: organization.name,
      hasContact: !!crmData,
    };
    if (result.hasContact) {
      const fieldDescriptions = organization.configuration.contact_profile_layout.details_entries;
      result.url = crmData.url;
      result.fields = fieldDescriptions.map(decription => this.adaptCRMField(decription, crmData.contact, crmData.url));
    }
    return result;
  }

  private adaptCRMField(description: CRMFieldDescription, contact, contactUrl): CRMFieldForProfile {
    const isReference = description.field_type === 'reference';
    const record = isReference ? contact[description.reference_to] : contact;
    const fieldType = isReference ? description.reference_field_type : description.field_type;
    const fieldName = isReference ? description.reference_field_name : description.field_name;

    // field_name can point to nested objects, e.g. 'phone.country_code', hence we can't just use record[field_name]
    const value = fieldName.split('.').reduce((acc, cur) => acc[cur], record || {});
    const result = {
      type: fieldType,
      label: description.display_label,
      value,
    };
    if (fieldType === 'id') {
      (result as any).url = contactUrl;
    }
    return result;
  }

  private getLinkedUsersAndCrmOrgs(siteId: number, userId: number): Observable<LinkedUserWithCRMOrgs> {
    const companyId = this.alcmeonContext.companyId;
    const url = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/sites/${siteId}/linked-users/${userId}?include_crm_orgs=true`;
    return this.http
      .get<any>(url, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  private getLinkedCRMContactList(userId: number, organizations: CRMOrg[]): Observable<CRMorgAndLinkedCRMData[]> {
    const companyId = this.alcmeonContext.companyId;
    const organizationsIds = organizations.map(org => org.id).join(',');
    const url = `${API_URL_PREFIX}/api/1.0/companies/${companyId}/crm/organizations/linked-contacts/${userId}?crmOrgIds=[${organizationsIds}]`;
    return this.asyncRequestService.get<LinkedCRMDataById>(url).pipe(
      map((linkedCRMDataById: LinkedCRMDataById) => {
        const crmOrgAndLinkedCRMDataArray: CRMorgAndLinkedCRMData[] = (organizations || []).map((org: CRMOrg) => ({
          organization: org,
          crmData: linkedCRMDataById[org.id],
        }));
        return crmOrgAndLinkedCRMDataArray;
      }),
      this.errorHandlingService.catchAlertAndThrowHttpError()
    );
  }

  private removeVIPActions(user: ExternalUser): void {
    const excludedTypes = ['tag_vip', 'untag_vip'];
    user.actions_allowed = user.actions_allowed.filter(action => !excludedTypes.includes(action.action_type));
  }

  private handleUserActiondError(response: HttpErrorResponse) {
    if (response.error && response.error.error) {
      this.errorHandlingService.modalAlertError(response, response.error.error);
    } else {
      this.errorHandlingService.toastAlertUnknownError(response);
    }
    return throwError(response);
  }
}
